import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { Button } from '@/components/atoms/Button';
import { useAsyncData } from '@/hooks/useAsyncData';
import { categoriesAPI } from '@/services/api/categories.api';
import { localizedField } from '@/utils/localizedField';
import { fireSideCannonsConfetti } from '@/utils/confetti';
import { useSlideActive } from '@/components/organisms/WelcomeOnboardingDialog/SlideActiveContext';
import styles from './AiGeneratingSlide.module.scss';

// Vídeo enmarcado (mismo encuadre que el slide final del welcome). TEMPORAL:
// reutilizamos el asset onboarding_06 hasta tener el vídeo dedicado de IA.
const GENERATING_VIDEO_BASE = 'https://lab.knwy-tools.com/challenges/onboarding/onboarding_06';
const GENERATING_VIDEO_SOURCES = [
  { src: `${GENERATING_VIDEO_BASE}.webm`, type: 'video/webm' },
  { src: `${GENERATING_VIDEO_BASE}.mp4`, type: 'video/mp4' },
];

/** Tiempo visible por frase y de la última antes de cerrar. Al menos 2 ciclos
 * de shimmer (1,5s cada uno) → ≥3000ms. */
const PHRASE_MS = 3000;
const LAST_PHRASE_MS = 3200;
/** Beat inicial antes de la primera frase (sensación de "pensando"). */
const START_DELAY_MS = 350;
/** Espera máxima por las categorías antes de arrancar igualmente (anti-cuelgue). */
const MAX_WAIT_MS = 2500;
/** Máximo de frases dedicadas a categorías (aunque el alumno tenga más). */
const MAX_CATEGORY_PHRASES = 4;
/** Retraso del botón "Empezar a aprender" tras aparecer el texto "¡listo!":
 *  se revela cuando el confeti (dos cañones, ~1,5s) está casi terminando. */
const CTA_REVEAL_DELAY_MS = 1350;

interface AiGeneratingSlideProps {
  /** Slugs de categorías elegidas; se usan (con su nombre localizado) en las frases. */
  categorySlugs: string[];
  /** Se llama al pulsar "Empezar a aprender" (cierra el onboarding y va al dashboard). */
  onDone: () => void;
}

/**
 * Pantalla final del onboarding de personalización: simula que una IA está
 * generando el contenido del alumno. Es un fake — las frases van pasando una a
 * una con efecto shimmer (estilo "trabajando" de un agente), usando las propias
 * categorías elegidas. Al terminar la secuencia se cierra solo (`onDone`).
 *
 * Solo arranca cuando su slide está activo (`useSlideActive`), porque todos los
 * slides del dialog se montan a la vez.
 */
export function AiGeneratingSlide({ categorySlugs, onDone }: AiGeneratingSlideProps) {
  const { t, i18n } = useTranslation();
  const reduceMotion = useReducedMotion();
  const active = useSlideActive();
  const categories = useAsyncData({ fetchFn: () => categoriesAPI.list() });

  const nameBySlug = useMemo(() => {
    const map = new Map<string, string>();
    (categories.data ?? []).forEach((c) => map.set(c.slug, localizedField(c, 'name', i18n.language)));
    return map;
  }, [categories.data, i18n.language]);

  // Secuencia de frases: genéricas + una por categoría (alternando el verbo),
  // cerrando con "casi listo". Si una categoría no resuelve nombre, se omite.
  const phrases = useMemo(() => {
    const names = categorySlugs
      .map((s) => nameBySlug.get(s))
      .filter((n): n is string => !!n)
      .slice(0, MAX_CATEGORY_PHRASES);
    const seq: string[] = [t('aiGeneratingSlide.phrases.analyzing')];
    names.forEach((name, i) => {
      const key = i % 2 === 0 ? 'selectingCategory' : 'vocabularyCategory';
      seq.push(t(`aiGeneratingSlide.phrases.${key}`, { category: name.toLowerCase() }));
    });
    seq.push(
      t('aiGeneratingSlide.phrases.level'),
      t('aiGeneratingSlide.phrases.exercises'),
      t('aiGeneratingSlide.phrases.plan'),
      t('aiGeneratingSlide.phrases.almost')
    );
    return seq;
  }, [categorySlugs, nameBySlug, t]);

  // Lista de frases congelada al arrancar, para que no cambie a mitad si las
  // categorías resuelven tarde. null = aún no arrancado.
  const [frozen, setFrozen] = useState<string[] | null>(null);
  const [index, setIndex] = useState(0);
  // Secuencia terminada: se muestra el mensaje final "¡listo!" y se dispara el
  // confeti. El botón NO sale a la vez (ver showCta).
  const [finished, setFinished] = useState(false);
  // El botón "Empezar a aprender" se revela con retraso respecto a "finished"
  // (cuando el confeti está casi acabando), no a la vez que el texto.
  const [showCta, setShowCta] = useState(false);
  // phrases más reciente, leída por el timer de arranque sin re-suscribirlo.
  const phrasesRef = useRef(phrases);
  phrasesRef.current = phrases;

  // Arranca cuando el slide está activo. Si las categorías ya cargaron usamos
  // el beat corto; si no, esperamos hasta MAX_WAIT_MS y arrancamos igual (con
  // las frases que haya — las de categoría se omiten si no resolvieron).
  useEffect(() => {
    if (!active || frozen) return;
    const delay = categories.isLoading ? MAX_WAIT_MS : START_DELAY_MS;
    const id = window.setTimeout(() => setFrozen(phrasesRef.current), delay);
    return () => window.clearTimeout(id);
  }, [active, frozen, categories.isLoading]);

  // Avanza de frase en frase; tras la última, marca como terminado (sale el botón).
  useEffect(() => {
    if (!frozen || finished) return;
    const isLast = index >= frozen.length - 1;
    const id = window.setTimeout(
      () => {
        if (isLast) setFinished(true);
        else setIndex((i) => i + 1);
      },
      isLast ? LAST_PHRASE_MS : PHRASE_MS
    );
    return () => window.clearTimeout(id);
  }, [frozen, index, finished]);

  // Al terminar la secuencia (finished): aparece el texto "¡listo!" y en ese
  // mismo instante disparamos el confeti "Side Cannons". El botón NO sale a la
  // vez: se revela algo más tarde (CTA_REVEAL_DELAY_MS), cuando el segundo cañón
  // está casi acabando, para que la celebración tenga su momento antes del CTA.
  // Con motion reducido no hay confeti que esperar → el botón aparece ya.
  // canvas-confetti respeta prefers-reduced-motion (no dibuja nada); al
  // desmontar el slide (cerrar el onboarding) cancela el bucle y limpia.
  useEffect(() => {
    if (!finished) return;
    const cancelConfetti = fireSideCannonsConfetti();
    const ctaTimer = window.setTimeout(
      () => setShowCta(true),
      reduceMotion ? 0 : CTA_REVEAL_DELAY_MS
    );
    return () => {
      cancelConfetti();
      window.clearTimeout(ctaTimer);
    };
  }, [finished, reduceMotion]);

  return (
    <div className={styles.slide}>
      <div className={styles.slide__video} aria-hidden="true">
        <video autoPlay muted playsInline preload="auto">
          {GENERATING_VIDEO_SOURCES.map((s) => (
            <source key={s.src} src={s.src} type={s.type} />
          ))}
        </video>
      </div>

      <h2 className={styles.slide__title}>{t('aiGeneratingSlide.title')}</h2>

      <div className={styles.slide__phraseWrap} aria-live="polite">
        {finished ? (
          <motion.span
            className={styles.slide__phrase}
            initial={reduceMotion ? false : { opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: reduceMotion ? 0 : 0.28, ease: 'easeOut' }}
          >
            {t('aiGeneratingSlide.ready')}
          </motion.span>
        ) : (
          <AnimatePresence mode="wait" initial={false}>
            <motion.span
              key={index}
              className={`${styles.slide__phrase} ${reduceMotion ? '' : styles['slide__phrase--shimmer']}`}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: reduceMotion ? 0 : 0.28, ease: 'easeOut' }}
            >
              {frozen ? frozen[index] : phrases[0]}
            </motion.span>
          </AnimatePresence>
        )}
      </div>

      {showCta && (
        <motion.div
          className={styles.slide__cta}
          initial={reduceMotion ? false : { opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: reduceMotion ? 0 : 0.4, delay: reduceMotion ? 0 : 0.15, ease: 'easeOut' }}
        >
          <Button variant="primary" size="lg" fullWidth onClick={onDone}>
            {t('aiGeneratingSlide.cta')}
          </Button>
        </motion.div>
      )}
    </div>
  );
}

AiGeneratingSlide.displayName = 'AiGeneratingSlide';
